import {verifiedFetch} from '@github-ui/verified-fetch'
import type {FeedbackSubmitHandler, FeedbackSubmitProps} from './types'
import {useCallback} from 'react'

interface useHandleFeedbackSubmitProps {
  path: string
  additionalParameters?: Record<string, string>
}

const buildFeedbackBody = (
  {type, commentId, feedbackChoice, textResponse, detailsOnly}: FeedbackSubmitProps,
  additionalParameters?: Record<string, string>,
) => {
  const body = new FormData()
  body.set('comment_id', commentId)
  body.set('feedback', type)
  if (detailsOnly) body.set('feedback_details_only', '1')
  if (additionalParameters) {
    for (const [key, value] of Object.entries(additionalParameters)) {
      body.set(key, value)
    }
  }
  for (const choice of feedbackChoice ?? []) body.append('feedback_choice[]', choice)
  if (textResponse) body.set('text_response', textResponse)
  return body
}

export const useHandleFeedbackSubmit = ({path, additionalParameters}: useHandleFeedbackSubmitProps) =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
  useCallback(
    props => {
      try {
        verifiedFetch(path, {method: 'POST', body: buildFeedbackBody(props, additionalParameters)})
      } catch {
        // nothing users can really do here, so just fail silently
      }
    },
    [path, additionalParameters],
  ) as FeedbackSubmitHandler

interface UseSubmitFeedbackReasonProps extends useHandleFeedbackSubmitProps {
  commentId: string
}

export type FeedbackReasonSubmitter = (feedbackChoice: string, textResponse?: string) => Promise<boolean>

/** Sends a single negative-feedback reason and resolves to whether the server accepted it. */
export const useSubmitFeedbackReason = ({
  path,
  commentId,
  additionalParameters,
}: UseSubmitFeedbackReasonProps): FeedbackReasonSubmitter =>
  useCallback(
    async (feedbackChoice, textResponse) => {
      try {
        const response = await verifiedFetch(path, {
          method: 'POST',
          body: buildFeedbackBody(
            {type: 'NEGATIVE', commentId, feedbackChoice: [feedbackChoice], textResponse, detailsOnly: true},
            additionalParameters,
          ),
        })
        return response.ok
      } catch {
        return false
      }
    },
    [path, commentId, additionalParameters],
  )
